import React from "react";
import {
  clearImpersonate,
  clearImpersonationReturnUrl,
  getImpersonationReturnUrl,
  impersonateAccount,
  impersonateUser,
} from "shared-ui/src/utils/clientmanagement";
import { isDemoAccountByAccountId } from "shared-ui/src/utils/demoDataHelpers";
import { isLocalCxHost } from "./urlHelper";

function toOrigin(url: string): string | null {
  try {
    return new URL(url).origin;
  } catch {
    return null;
  }
}

/**
 * Returns the saved CS page the user was on before impersonation started
 * (e.g. My Accounts with its filter/sort/page/search state, an account
 * details page, etc.), provided it is safe to redirect to — i.e. it resolves
 * to the current origin or the configured CS origin. This guards against an
 * open redirect via a tampered/stale storage value.
 */
function getSafeCsReturnUrl(csUrl: string | undefined): string | null {
  const savedUrl = getImpersonationReturnUrl();

  if (!savedUrl) {
    return null;
  }

  try {
    const parsedSavedUrl = new URL(savedUrl);
    const allowedOrigins = new Set([window.location.origin]);

    if (csUrl) {
      const csOrigin = toOrigin(csUrl);
      if (csOrigin) {
        allowedOrigins.add(csOrigin);
      }
    }

    if (!allowedOrigins.has(parsedSavedUrl.origin)) {
      return null;
    }

    return parsedSavedUrl.href;
  } catch {
    return null;
  }
}

/**
 * Clears impersonation state and returns the user to Customer Success.
 *
 * In production, `useInternalUserRedirect` fires after localStorage is cleared
 * and redirects the internal user to CS automatically. On local/dev hosts that
 * hook is disabled (to allow CX standalone dev), so we pass the CS URL directly.
 */
export function clearImpersonateAndReturnToCs(): void {
  const csUrl = import.meta.env.CUSTOMER_SUCCESS_UI as string | undefined;
  const savedReturnUrl = getSafeCsReturnUrl(csUrl);
  const fallbackUrl = isLocalCxHost() && csUrl ? csUrl : window.location.origin;

  clearImpersonationReturnUrl();

  clearImpersonate(savedReturnUrl ?? fallbackUrl);
}

export const handleImpersonateAccountWithoutConfirmation = (
  e: React.SyntheticEvent | null,
  accountId: number | null,
) => {
  if (e && typeof e.persist === "function") {
    e.persist();
  }
  if (accountId) {
    impersonateAccount(accountId, isDemoAccountByAccountId(accountId));
  }
};

export const handleImpersonateUser = (
  e: React.SyntheticEvent | null,
  userId: string | null,
  demoData?: boolean,
) => {
  if (e && typeof e.persist === "function") {
    e.persist();
  }

  if (!userId) {
    return;
  }

  impersonateUser({ userId }, demoData);
};
